import { GetPublicOrganizationResponse, LoginOrigin } from '../../types';
import { ensureFacebookSdk, ensureGoogleIdentity } from '../../helpers/vendor-scripts';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { detectMobileViewport, inIframe } from '../../helpers/device';
import { getAccount, getOrganizationRoles, login, setAccount, upgradeFacebookToken, upgradeGoogleToken, upgradeToken } from '../../helpers/api/account-api';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo, {} from '../../helpers/state/account-info';
import { getGoogleProfile } from '../../helpers/api/google-api';
import { getMainPage } from '../../helpers/page-name';
import { getPublicOrganizations } from '../../helpers/api/organization-api';
import { getQueryStringParameters } from '../../helpers/urls';
import { logWithAmplitude } from '../../helpers/api/api';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';
import { updateTopicAndPointsWithEmail } from '../../helpers/api/topics-api';
import urlInfo from '../../helpers/state/url-info';

@customElement('signin-modal-component')
export class SigninModalComponent extends LitElement {
    static amplitude_callsite: string = 'Sign In Modal';

    @property({ type: String })
    emailAddress?: string;


    @property({ type: Boolean })
    has_pending_bookmark: boolean = false;

    @property({ type: Boolean })
    updateEmail: boolean = false;

    @property({ type: Object })
    facebookAuth: Object = { scope: 'email' };

    @property({ type: Boolean })
    show_one_time_code: boolean = false;

    @property({ type: String })
    one_time_code?: string;

    @state()
    organizations: GetPublicOrganizationResponse[] = [];

    @state()
    isLoading: boolean = false;

    @state()
    isAppSumo: boolean = false;

    @state()
    errorMessage?: string;

    @state()
    isCheckout: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountInfo = new StateController(this, accountInfo);
    private readonly bindUrlInfo = new StateController(this, urlInfo);

    handleChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    handleChangeOneTimeCode(e: any) {
        this.one_time_code = e.detail.value;
    }

    private async handleNewEmail() {
        if (accountInfo.isAuthenticated) {
            await accountInfo.logout();
        }
        this.show_one_time_code = false;
        this.requestUpdate();
    }

    private async handleSubmit() {
        const email = this.emailAddress ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(SigninModalComponent.amplitude_callsite, email);
            if (this.updateEmail) {
                await this.updateMapWithEmail(email);
            }

            this.show_one_time_code = true;
            this.requestUpdate();
        }, 'An error has occurred.  Context: could not request an authentication token.');
    }

    private async handleCheckOrganizations() {
        this.isLoading = true;
        const email = this.emailAddress ?? '';
        try {
            // Authenticate in the backend with one time code
            const rolesData = await getOrganizationRoles(
                SigninModalComponent.amplitude_callsite,
                email,
            );
          if (rolesData?.length > 0) {
                const organizationIds = rolesData.map(role => role.role?.OrganizationRoleType?.organization_id?.$oid);

                // Using Promise syntax
                getPublicOrganizations(SigninModalComponent.amplitude_callsite, organizationIds)
                    .then(organizations => {
                        this.organizations = organizations.filter((org): org is GetPublicOrganizationResponse => org !== undefined);
                        this.show_one_time_code = true;
                        this.requestUpdate();
                    })
                    .catch(err => {
                        console.error('Error fetching organizations:', err);
                        // Handle the error appropriately
                    });
            } else {
                await this.postUpgradeUpdates();
            }
        } catch(err) {
            this.organizations = [];
        }
        this.isLoading = false;
    }

    private async handleSubmitOneTimeCode(organization_id?: string) {
        const email = this.emailAddress ?? '';
        const one_time_code = this.one_time_code ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                SigninModalComponent.amplitude_callsite,
                email,
                one_time_code,
                undefined,
                LoginOrigin.SigninModule
            );
            await this.handleCheckOrganizations();
        }, 'An error has occurred.  Context: could not upgrade the authentication token.');
    }

    private async postOrganizationUpgrade(organization_id: string) {
        // set local storage
        localStorage.setItem('organization_id', organization_id);
        await accountInfo.instantiateAccountData();
        this.handleReirectAfterUpgrade();
    }

    private async upgradeFirstOrganization() {
        // TODO: Make sure it opens to the first one they have access to
        if (urlInfo.urlOrganization) {
            await this.postOrganizationUpgrade(urlInfo.urlOrganization._id.$oid);
        } else {
            await this.postOrganizationUpgrade(this.organizations[0]._id.$oid);
        }
    }

    private async postUpgradeUpdates() {
        // Instantiate account cache
        await accountInfo.instantiateAccountData();

        // Add bookmark if flow was started from bookmark button
        if (this.has_pending_bookmark) {
            const queryString = getQueryStringParameters();
            const topic_id = queryString.get('topic_id') ?? topicData.topicId;
            const topic_key = queryString.get('topic_key') ?? topicData.topicKey;
            if (topic_id !== undefined) {
                await accountInfo.bookmarkTopic(topic_id, topic_key);
            }
        }

        this.handleReirectAfterUpgrade();
    }

    private handleReirectAfterUpgrade() {
        if (window.location.hash.startsWith('#login')) {
            const queryString = getQueryStringParameters();
            const callback_url = queryString.get('callback_url');
            if (callback_url) {
                window.location.assign(callback_url);
            } else {
                window.location.assign('/#home');
            }
        } else {
            this.dispatchEvent(new CustomEvent('close'));
        }
    }

    private async updateMapWithEmail(email: string) {
        const queryString = getQueryStringParameters();
        const topic_id = queryString.get('topic_id') ?? topicData.topicId;
        const topic_key = queryString.get('topic_key') ?? topicData.topicKey;

        if (topic_id && topic_key) {
            await updateTopicAndPointsWithEmail(
                SigninModalComponent.amplitude_callsite,
                topic_id, email,
                topic_key,
                pointCache.allPoints.length,
            );
        }
    }

    private async facebookLogin() {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await ensureFacebookSdk();
            window.FB.login((response: any) => {
                if (response.status === 'connected') {
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    window.FB.api('/me?', { fields: 'email,name' }, async (me_response: any) => {
                        if (me_response.email === undefined) {
                            getMainPage().errorToastElement.showToast('Facebook Login requires email.');
                        } else {
                            // Authenticate in the backend with Facebook email and token
                            await upgradeFacebookToken(
                                SigninModalComponent.amplitude_callsite,
                                me_response.email,
                                response.authResponse.accessToken
                            );
                            if (this.updateEmail) {
                                await this.updateMapWithEmail(me_response.email);
                            }

                            // Grab first name from Facebook
                            if (me_response.name !== undefined) {
                                const account = await getAccount(
                                    SigninModalComponent.amplitude_callsite
                                );
                                if (account !== undefined) {
                                    if (account.full_name === undefined) {
                                        account.full_name = me_response.name;
                                        await setAccount(
                                            SigninModalComponent.amplitude_callsite,
                                            account
                                        );
                                    }
                                }
                            }

                            this.emailAddress = me_response.email;
                            await this.handleCheckOrganizations();
                        }
                    });
                } else {
                    getMainPage().errorToastElement.showToast(
                        'Facebook Login did not return connected response.'
                    );
                }
            }, this.facebookAuth);
            this.facebookAuth = { scope: 'email', auth_type: 'rerequest' };
        }, 'An error has occurred.  Context: Facebook login failed.');
    }

    private async googleLogin() {
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await ensureGoogleIdentity();
            const client = window.google.accounts.oauth2.initTokenClient({
                client_id:
                    '749784737867-o1o7gtt25j0d3j54d605h25caojn1vo5.apps.googleusercontent.com',
                scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email',
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                callback: async (tokenResponse) => {
                    // Get google email
                    const profile = await getGoogleProfile(
                        SigninModalComponent.amplitude_callsite,
                        tokenResponse.access_token
                    );
                    if (profile.email === undefined) {
                        getMainPage().errorToastElement.showToast(
                            'Google Login failed to retrieve email information.'
                        );
                    }

                    // Authenticate in the backend with Google email and token
                    await upgradeGoogleToken(
                        SigninModalComponent.amplitude_callsite,
                        profile.email,
                        tokenResponse.access_token
                    );
                    if (this.updateEmail) {
                        await this.updateMapWithEmail(profile.email);
                    }

                    // Grab first name from Google
                    if (profile.name !== undefined) {
                        const account = await getAccount(
                            SigninModalComponent.amplitude_callsite
                        );
                        if (account !== undefined) {
                            if (account.full_name === undefined) {
                                account.full_name = profile.name;
                                await setAccount(
                                    SigninModalComponent.amplitude_callsite,
                                    account
                                );
                            }
                        }
                    }
                    this.emailAddress = profile.email;
                    await this.handleCheckOrganizations();
                },
            });
            client.requestAccessToken();
        }, 'An error has occurred.  Context: Google login failed.');
    }

    private openTopic() {
        logWithAmplitude(
            SigninModalComponent.amplitude_callsite,
            'Open Command Center from Safari Bookmark'
        );
        window.open(window.location.hash);
    }

    private gotoHome() {
        window.location.assign('https://proxi.co');
    }

    get message() {
        if (this.errorMessage) {
            return this.errorMessage;
        } else if (urlInfo.urlOrganization) {
            return ``;
        } else {
            return 'Proxi is a world class mapping platform that enables you to create, customize, and share interactive maps with challenges, local activations, and more.';
        }
    }

    get headerMessage() {
        if (this.errorMessage) {
            return 'We might have a problem...';
        } else if (this.isAppSumo) {
            return `Welcome to Proxi. Let's activate your license`;
        } else if (this.isCheckout) {
            return 'Welcome to Proxi. Please sign in to finish before subscribing.';
        } else if (urlInfo.urlOrganization) {
            return `${urlInfo.urlOrganization.name} Login`;
        } else {
            return 'Welcome to Proxi';
        }
    }


    renderFirstScreen() {
        return html`
            <div class="rightSection">
                <h1 class="modal-title">
                    ${this.headerMessage}
                </h1>
                ${this.isAppSumo || urlInfo.urlOrganization ? html`<br><br>` : html`
                    <p class="login-description">
                        ${this.message}</p>
                    <div class="socialButtonWrapper">
                        <form-button
                            variant="social"
                            style="flex: 1"
                            size="sm"
                            ?fullWidth=${true}
                            @click="${this.googleLogin}"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                            >
                                <path
                                    d="M9 7.895h7.283q0.121 0.673 0.121 1.285 0 2.179 -0.914 3.893t-2.606 2.677 -3.882 0.965q-1.577 0 -3.003 -0.608t-2.461 -1.643 -1.643 -2.461 -0.608 -3.003 0.608 -3.003 1.643 -2.461 2.461 -1.643 3.003 -0.608q3.013 0 5.173 2.019l-2.099 2.019q-1.235 -1.196 -3.074 -1.196 -1.296 0 -2.396 0.653t-1.743 1.773 -0.643 2.446 0.643 2.446 1.743 1.773 2.396 0.653q0.874 0 1.607 -0.241t1.205 -0.603 0.824 -0.824 0.517 -0.874 0.226 -0.784h-4.379v-2.652z"
                                />
                            </svg>
                            ${detectMobileViewport() ? '' : 'Sign in with Google'}
                        </form-button>
                        <form-button
                            variant="social"
                            style="flex: 1"
                            size="sm"
                            ?fullWidth=${true}
                            @click="${this.facebookLogin}"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 18 18"
                                width="18"
                                height="18"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M13.5 6.188H10.125v-2.25a1.125 1.125 0 0 1 1.125 -1.125h1.125V0H10.125a3.375 3.375 0 0 0 -3.375 3.375v2.813H4.5V9h2.25v9h3.375V9h2.25l1.125 -2.813z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            ${detectMobileViewport() ? '' : 'Sign in with Facebook'}
                        </form-button>
                    </div>
                    <div class="dividerSection flexContainer alignCenter">
                        <hr />
                        <div>OR</div>
                        <hr />
                    </div>
                `}
                ${navigator.vendor === 'Apple Computer, Inc.' && inIframe()
                    ? html`
                        <div>
                            <form-button
                                type="submit"
                                id="signinBtn"
                                @click="${this.openTopic}"
                                ?fullWidth=${true}
                            >
                                Open map in new window to login with email
                            </form-button>
                        </div>
                    `
                    : html`
                        <div>
                            <form-input
                                id="name"
                                type="email"
                                label="${this.isAppSumo ? 'Verify Your AppSumo Email to Log In': 'Email Address'}"
                                placeholder="Enter your email"
                                value="${this.emailAddress ?? ''}"
                                ?disabled=${this.isAppSumo}
                                @change="${this.handleChangeEmailAddress}">
                            </form-input>
                            <div class="flexContainer">
                                <span>You will receive a one time code that you will enter here.</span>
                            </div>
                            <form-button
                                type="submit"
                                id="signinBtn"
                                @click="${this.handleSubmit}"
                                ?fullWidth=${true}>
                                Send My One Time Code
                            </form-button>
                            ${this.isAppSumo || urlInfo.urlOrganization ? '' : html`
                                <div class="tryAgainLink" @click=${this.gotoHome}>
                                    <mwc-icon>arrow_back</mwc-icon>
                                    <span>Back to Proxi Home Page</span>
                                </div>
                            `}
                        </div>
                    `}
            </div>
        `;
    }

    renderSecondScreen() {
        return html`
            <div class="rightSection textCenter">
                <h1 class="modal-title">Email Sent</h1>
                <p class="code-description">Check your email <b>${this.emailAddress}</b> and enter the 6 digit code here:</p>
                <form-input
                    id="name"
                    type="string"
                    label="Verification Code"
                    placeholder="000000"
                    value="${this.one_time_code ?? ''}"
                    @change="${this.handleChangeOneTimeCode}">
                </form-input>
                <form-button
                    type="submit"
                    id="signinBtn"
                    @click="${this.handleSubmitOneTimeCode}"
                    ?fullWidth=${true}>
                    Log In
                </form-button>
                <div class="tryAgainLink">
                    Didn't receive the email? Check your spam folder or <span class="click-resend" @click=${this.handleSubmit}>click to resend</span>.
                </div>
                ${this.isAppSumo ? '' : html`
                    <div class="tryAgainLink" @click=${this.handleNewEmail}>
                        <mwc-icon>arrow_back</mwc-icon>
                        <span>Try Another Email</span>
                </div>
                `}
            </div>
        `;
    }

    render() {
        return html`
            <div class="flexContainer alignCenter signinModalContent">
                ${this.isLoading ? html`
                    <div class="rightSection textCenter">
                        <loading-element
                            loadingString="Checking your account..."
                        ></loading-element>
                    </div>`
                 : html`
                ${this.show_one_time_code ?
                    this.organizations.length > 0 ? this.upgradeFirstOrganization() : this.renderSecondScreen()
                    : this.renderFirstScreen()}`
                }
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .signinModalContent {
                color: var(--theme-input-color);
                background: var(--theme-white-color);
            }

            .flexContainer {
                display: flex;
            }

            .alignCenter {
                align-items: center;
            }

            .justifyCenter {
                justify-content: center;
            }

            .justifySpaceBetween {
                justify-content: space-between;
            }

            .rightSection {
                flex: 1;
                padding: 2rem;
            }

            .textCenter {
                text-align: center;
            }

            .modal-title {
                margin: 0;
                font-size: 1.5rem;
            }

            .login-description {
                font-size: 0.8rem;
                margin: 1rem 0;
            }

            .code-description {
                font-size: 1rem;
                margin-top: 0.5rem;
                margin-bottom: 1.5rem;
                line-height: 1.5;
            }

            .dividerSection {
                margin: 1rem 0;
            }

            .dividerSection div {
                padding: 0 1rem;
            }

            .dividerSection hr {
                width: 100%;
                border: none;
                background: var(--theme-border-color);
                height: 1px;
            }

            .socialButtonWrapper {
                margin-bottom: 1rem;
                display: flex;
                flex-direction: row;
                gap: 1rem;
            }

            #signinBtn {
                display: block;
                margin-top: 1rem;
                --btn-padding-x: 2rem;
            }

            .tryAgainLink {
                cursor: pointer;
                text-decoration: none;
                font-weight: 500;
                margin-top: 1rem;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 0.5rem;
            }

            .teamsInfo {
                text-decoration: none;
                font-weight: 500;
                margin-top: 1rem;
                display: flex;
                align-items: center;
                text-align: center;
                gap: 0.5rem;
            }

            mwc-icon {
                font-size: 1rem;
            }

            .click-resend {
                color: var(--main-invalid-color);
                font-weight: 600;
            }

            .accounts-list {
                margin: 1rem 0;
                display: inline-flex;
                flex-wrap: wrap;
                justify-content: center;
                gap: 10px;
            }

            .account-card {
                padding: 0.5rem;
                text-align: center;
                cursor: pointer;
                font-size: 18px;
                font-weight: 700;
                border-radius: 6px;
                box-shadow: 0px 0px 0.5rem rgba(0, 0, 0, 0.25);
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            form-button svg {
                margin-right: 0.75rem;
                fill: var(--main-primary-color);
                transition: fill 0.2s ease-in-out;
            }

            form-button:hover svg {
                fill: var(--theme-white-color);
            }

            @media (min-width: 961px) {
                .desktopOnly {
                    display: block;
                }
            }

            @media (max-width: 960px) {
                .desktopOnly {
                    display: none;
                }

                .signinModalContent {
                    flex-direction: column;
                }
            }

            @media (max-width: 550px) {
                .rightSection {
                    padding-left: 0;
                    padding-right: 0;
                }

                p {
                    font-size: 0.7rem;
                    line-height: 0.7rem;
                }

                .modal-title {
                    margin: 0;
                    font-size: 1rem;
                }

                .login-description {
                    font-size: 0.7rem;
                    margin: 1rem 0;
                }
            }
        `;
    }
}
